import { STEMS, type StemId, type StemInputs } from '../audio/types';
import { sha256 } from '../audio/hash';
export interface SavedSong {
  id: string; version: string; title: string; key: string; bpm: number | null;
  size: number; lastPlayed: number; savedAt: number;
  stems: Record<StemId, { name: string; hash: string; size: number }>;
}
const LIMIT = 1_000_000_000, AGE = 30 * 86400000;
function result<T>(r: IDBRequest<T>): Promise<T> { return new Promise((resolve,reject)=>{r.onsuccess=()=>resolve(r.result);r.onerror=()=>reject(r.error);}); }
function done(tx: IDBTransaction): Promise<void> { return new Promise((resolve,reject)=>{tx.oncomplete=()=>resolve();tx.onabort=tx.onerror=()=>reject(tx.error??new Error('Offline storage failed.'));}); }
export class OfflineSongs {
  private opening?: Promise<IDBDatabase>;
  private db() {
    return this.opening ??= new Promise<IDBDatabase>((resolve,reject)=>{
      const r=indexedDB.open('hope-mixer-offline',1);
      r.onupgradeneeded=()=>{r.result.createObjectStore('songs',{keyPath:'id'});r.result.createObjectStore('stems');};
      r.onsuccess=()=>{r.result.onversionchange=()=>r.result.close();resolve(r.result);};r.onerror=()=>reject(r.error);
      r.onblocked=()=>reject(new Error('Close other Hope Mixer tabs to update offline storage.'));
    });
  }
  async list(): Promise<SavedSong[]> { const db=await this.db(); return result(db.transaction('songs').objectStore('songs').getAll()); }
  async remove(song: SavedSong): Promise<void> {
    const db=await this.db(),tx=db.transaction(['songs','stems'],'readwrite'), finished=done(tx);
    tx.objectStore('songs').delete(song.id);for(const id of STEMS)tx.objectStore('stems').delete(`${song.id}/${song.version}/${id}`);await finished;
  }
  async save(inputs: StemInputs, metadata: {title:string;key:string;bpm:number|null}, identity?: string): Promise<SavedSong> {
    if(STEMS.some(id=>!inputs[id]))throw new Error('Load all four stems before saving offline.');
    const records=await this.list(), size=STEMS.reduce((n,id)=>n+(inputs[id]!.encodedSize??inputs[id]!.bytes?.byteLength??0),0);
    if(size>LIMIT)throw new Error('This song exceeds the 1 GB offline limit.');
    const id=identity??crypto.randomUUID(),version=crypto.randomUUID(),stems={} as SavedSong['stems'], db=await this.db();
    let used=records.filter(song=>song.id!==id).reduce((n,s)=>n+s.size,0);
    for(const old of records.filter(s=>s.id!==id&&Date.now()-s.lastPlayed>AGE).sort((a,b)=>a.lastPlayed-b.lastPlayed)){
      if(used+size<=LIMIT)break;await this.remove(old);used-=old.size;
    }
    if(used+size>LIMIT)throw new Error('Offline storage is full. Remove a saved song first.');
    try {
      // Write one encoded stem at a time. Publish the manifest only after all four commit.
      for(const stem of STEMS){
        const input=inputs[stem]!,bytes=input.bytes?.slice(0)??await input.read?.();if(!bytes)throw new Error('Missing stem data.');
        const hash=sha256(bytes);if(input.expectedHash&&hash!==input.expectedHash.toLowerCase())throw new Error('Stem changed while saving. Reload the song.');
        stems[stem]={name:input.name,hash,size:bytes.byteLength};
        const tx=db.transaction('stems','readwrite'),finished=done(tx);tx.objectStore('stems').put(new Blob([bytes]),`${id}/${version}/${stem}`);await finished;
      }
      const song:SavedSong={id,version,...metadata,stems,size:STEMS.reduce((n,s)=>n+stems[s].size,0),savedAt:Date.now(),lastPlayed:Date.now()};
      if(used+song.size>LIMIT)throw new Error('Offline storage exceeds 1 GB.');
      const tx=db.transaction(['songs','stems'],'readwrite'),finished=done(tx),old=records.find(s=>s.id===id);
      tx.objectStore('songs').put(song);if(old)for(const stem of STEMS)tx.objectStore('stems').delete(`${id}/${old.version}/${stem}`);await finished;
      return song;
    }catch(error){
      const tx=db.transaction('stems','readwrite'),finished=done(tx);for(const stem of STEMS)tx.objectStore('stems').delete(`${id}/${version}/${stem}`);await finished;
      if(error instanceof DOMException&&error.name==='QuotaExceededError')throw new Error('This browser has insufficient offline space. Remove a saved song or free device storage.');
      throw error;
    }
  }
  inputs(song:SavedSong):StemInputs {
    return Object.fromEntries(STEMS.map(id=>[id,{name:song.stems[id].name,expectedHash:song.stems[id].hash,encodedSize:song.stems[id].size,read:async()=>{
      const db=await this.db(),blob:Blob|undefined=await result(db.transaction('stems').objectStore('stems').get(`${song.id}/${song.version}/${id}`));
      if(!blob)throw new Error('Offline stem is missing. Download the song again.');return blob.arrayBuffer();
    }}]));
  }
  async played(song:SavedSong):Promise<void>{const db=await this.db(),tx=db.transaction('songs','readwrite'),finished=done(tx);const request=tx.objectStore('songs').get(song.id);request.onsuccess=()=>{const current=request.result as SavedSong|undefined;if(current?.version===song.version)tx.objectStore('songs').put({...current,lastPlayed:Date.now()});};await finished;}
}
